import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getUrlFromPathname } from '@/redux/modules/config';
import { LotRecommendationType } from '@/utils/amplitude/analytics/item/trackLotRecommendationClicked.analytics';
import { trackLotRecommendationSeenAnalytics } from '@/utils/amplitude/analytics/item/trackLotRecommendationSeen.analytics';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

type Props = {
    children: React.ReactNode;
    recommendationIds?: number[];
    recommendationType: LotRecommendationType;
};

export const VISIBILITY_THRESHOLD = 0.25;
export const VISIBILITY_DEBOUNCE_MS = 500;

export const RecommendationVisibilityWrapper = ({ children, recommendationIds, recommendationType }: Props) => {
    const moduleRef = useRef<HTMLDivElement>(null);
    const [hasVisibilityBeenTracked, setHasVisibilityBeenTracked] = useState(false);
    const [debouncedIsVisible, setDebouncedIsVisible] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const location = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, location.pathname));

    // Stabilize recommendationIds by value so effects don't re-trigger on every new array reference
    const recommendationIdsKey = recommendationIds?.join(',') ?? '';
    const stableRecommendationIds = useMemo(() => recommendationIds, [recommendationIdsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const el = moduleRef.current;
        if (!el) {
            return;
        }

        const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting), {
            threshold: VISIBILITY_THRESHOLD,
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        setHasVisibilityBeenTracked(false);
        setDebouncedIsVisible(false);
    }, [recommendationIdsKey]);

    useEffect(() => {
        if (isVisible) {
            const timer = setTimeout(() => setDebouncedIsVisible(true), VISIBILITY_DEBOUNCE_MS);
            return () => clearTimeout(timer);
        }
        setDebouncedIsVisible(false);
    }, [isVisible, recommendationIdsKey]);

    useEffect(() => {
        if (debouncedIsVisible && !hasVisibilityBeenTracked && stableRecommendationIds?.length) {
            trackLotRecommendationSeenAnalytics({
                pageName: getAmplitudePageNameFromPath(location.pathname),
                recommendationIds: stableRecommendationIds,
                recommendationName: recommendationType,
                url,
            });
            setHasVisibilityBeenTracked(true);
        }
    }, [
        debouncedIsVisible,
        hasVisibilityBeenTracked,
        recommendationType,
        stableRecommendationIds,
        location.pathname,
        url,
    ]);

    return <div ref={moduleRef}>{children}</div>;
};
